// ============================================================
//  Bragsheet React — firestore.js
//  Lazy accessor for the Firestore SDK. `firebase/firestore` is the largest
//  Firebase entry point (the webchannel transport alone dwarfs auth), and only
//  the data screens ever touch it — so it is dynamically imported on the first
//  actual data call rather than at boot. Nothing in the app statically imports
//  'firebase/firestore'; the data layer awaits loadFirestore() instead, which
//  keeps the Firestore chunk off the startup and first-render path and lets the
//  app shell paint before it loads.
//
//  The dynamic import resolves once and is cached, so subsequent data calls pay
//  no extra cost. It returns the db handle plus the SDK's functions, so callers
//  destructure exactly what they need (collection, query, doc, getDocs, …).
//
//  OFFLINE PERSISTENCE (2026-09-29): the db is created with initializeFirestore
//  + persistentLocalCache instead of a bare getFirestore(), so Firestore keeps
//  an IndexedDB-backed copy of everything the user has read and QUEUES writes
//  made while offline, replaying them automatically when the connection returns.
//  This is the web equivalent of the Android offline queue — the SDK owns the
//  queue; we do not build one. persistentMultipleTabManager shares that single
//  cache safely across tabs (no "only one tab may enable persistence" error).
//  Notes / limits worth remembering:
//    - Only data the user has already opened is readable offline.
//    - A write made offline is applied to the local cache immediately (listeners
//      fire optimistically), but the setDoc/addDoc PROMISE does not resolve until
//      the write reaches the server — so callers must not block the UI on it
//      while offline (see the Add-Win offline-save follow-up).
//    - initializeFirestore MUST run before any getFirestore(app); this module is
//      the app's single Firestore init point, and loadFirestore() is memoised, so
//      that ordering holds.
//    - Where IndexedDB is unavailable (some private-window / embedded webviews)
//      persistentLocalCache init can throw; we fall back to the default in-memory
//      cache so the app still works online, just without offline support.
// ============================================================

import { app } from './firebase.js';

let _fsPromise = null;

/**
 * Create the Firestore handle with an IndexedDB-backed persistent cache,
 * falling back to the default (memory) cache if persistence can't initialise.
 * @param {object} fs the resolved `firebase/firestore` module
 * @returns {object} the Firestore db handle
 */
function makeDb(fs) {
  try {
    return fs.initializeFirestore(app, {
      localCache: fs.persistentLocalCache({
        tabManager: fs.persistentMultipleTabManager(),
      }),
    });
  } catch (err) {
    // IndexedDB unavailable/blocked (e.g. some private windows or webviews),
    // or Firestore was already initialised elsewhere. Degrade gracefully to the
    // default cache — the app stays fully functional online.
    console.warn('[Firestore] Offline persistence unavailable, using default cache:', err);
    return fs.getFirestore(app);
  }
}

/**
 * Resolve to `{ db, ...firestoreSdk }`, loading firebase/firestore on first use.
 * @returns {Promise<object>} the db handle merged with the Firestore SDK exports
 */
export function loadFirestore() {
  if (!_fsPromise) {
    _fsPromise = import('firebase/firestore').then((fs) => ({
      db: makeDb(fs),
      ...fs,
    }));
  }
  return _fsPromise;
}
